/**
 * Guessing what a transaction is: which category it belongs in, and which tags
 * it should wear.
 *
 * The model is **linear and trained on your own ledger** — nothing is shipped
 * pre-trained, nothing is downloaded, and a fresh install suggests nothing
 * until you have filed enough rows for it to have an opinion. One logistic
 * regression per label, one-vs-rest, over sparse features taken from the
 * description, the amount and the direction.
 *
 * Linear on purpose. A bank description is a bag of tokens and the signal is
 * mostly "does the word LIDL appear", which a linear model reads off directly;
 * it also trains in milliseconds in a browser tab, and — the part that matters
 * when it is wrong — you can ask it *why* and get back the words that decided,
 * with their weights.
 *
 * Everything that could be tuned is a parameter with a default here rather than
 * a constant buried in the maths, so a run can be repeated and compared.
 */
import { round2 } from './money'

/**
 * Every knob and the range it may take. What each one *means* is a sentence in
 * the catalogues under `lab.<key>` — this file holds no words.
 */
export const PARAMETERS = [
  {
    key: 'learningRate',
    min: 0.01,
    max: 2,
    step: 0.01,
    default: 0.5,
  },
  {
    key: 'epochs',
    min: 1,
    max: 60,
    step: 1,
    default: 12,
  },
  {
    key: 'l2',
    min: 0,
    max: 0.1,
    step: 0.0005,
    default: 0.002,
  },
  {
    key: 'threshold',
    min: 0.05,
    max: 0.95,
    step: 0.05,
    default: 0.5,
  },
  {
    key: 'minExamples',
    min: 1,
    max: 20,
    step: 1,
    default: 3,
  },
  {
    key: 'minTokenLength',
    min: 1,
    max: 6,
    step: 1,
    default: 2,
  },
  {
    key: 'maxTags',
    min: 1,
    max: 8,
    step: 1,
    default: 3,
  },
]

export const DEFAULTS = Object.fromEntries(PARAMETERS.map((p) => [p.key, p.default]))

/** Which kinds of feature to draw; described in the catalogues like the rest. */
export const FEATURE_FLAGS = [
  {
    key: 'bigrams',
    default: true,
  },
  {
    key: 'prefixes',
    default: true,
  },
  {
    key: 'amount',
    default: true,
  },
  {
    key: 'weekday',
    default: false,
  },
]

export const FEATURE_DEFAULTS = Object.fromEntries(FEATURE_FLAGS.map((f) => [f.key, f.default]))

/** The settings a run is made of, defaults filled in for anything absent. */
export const withDefaults = (options = {}) => ({
  ...DEFAULTS,
  ...FEATURE_DEFAULTS,
  ...Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined && v !== null)),
})

const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** Words, as a statement writes them: punctuation and card numbers are not words. */
export function tokenise(description, minLength = 2) {
  return fold(description)
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= minLength && !/^\d+$/.test(word))
}

/**
 * A transaction as a sparse map of feature → value.
 *
 * Every feature is a named string so a weight can be read back and explained;
 * nothing here is a magic index into an array.
 */
export function features(tx, options = {}) {
  const o = withDefaults(options)
  const out = new Map()
  const bump = (name, value = 1) => out.set(name, (out.get(name) ?? 0) + value)

  bump('bias')
  bump(tx.amount > 0 ? 'dir:in' : 'dir:out')

  const words = tokenise(tx.description, o.minTokenLength)
  for (const word of words) bump(`w:${word}`)
  if (o.bigrams) {
    for (let i = 1; i < words.length; i++) bump(`b:${words[i - 1]}_${words[i]}`)
  }
  if (o.prefixes) {
    for (const word of words) if (word.length > 4) bump(`p:${word.slice(0, 4)}`)
  }
  if (o.amount) {
    const size = Math.abs(Number(tx.amount) || 0)
    bump(`mag:${size < 1 ? 0 : Math.floor(Math.log10(size))}`)
  }
  if (o.weekday && tx.date) {
    const [y, m, d] = String(tx.date).split('-').map(Number)
    bump(`dow:${new Date(y, m - 1, d).getDay()}`)
  }
  return out
}

const sigmoid = (z) => 1 / (1 + Math.exp(-z))

/** Deterministic shuffling, so the same settings give the same model twice. */
function shuffled(items, seed = 1) {
  const out = [...items]
  let state = seed >>> 0 || 1
  for (let i = out.length - 1; i > 0; i--) {
    state = (state * 1664525 + 1013904223) >>> 0
    const j = state % (i + 1)
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/**
 * One binary logistic regression, trained by plain SGD.
 *
 * `positives` is a Set of example indices that carry the label. Weight decay is
 * applied only to the features an example actually has — over a sparse bag of
 * words, decaying every weight on every step costs more than the regularising
 * is worth.
 */
function trainOne(rows, positives, o, seed) {
  const weights = new Map()
  const order = shuffled(rows.map((_, i) => i), seed)
  for (let epoch = 0; epoch < o.epochs; epoch++) {
    for (const i of order) {
      const x = rows[i]
      let z = 0
      for (const [name, value] of x) z += (weights.get(name) ?? 0) * value
      const error = (positives.has(i) ? 1 : 0) - sigmoid(z)
      for (const [name, value] of x) {
        const w = weights.get(name) ?? 0
        weights.set(name, w + o.learningRate * (error * value - o.l2 * w))
      }
    }
  }
  return weights
}

const score = (weights, x) => {
  let z = 0
  for (const [name, value] of x) z += (weights.get(name) ?? 0) * value
  return sigmoid(z)
}

/**
 * Trains a model over labelled transactions.
 *
 * @param {object[]} examples  `{ tx, category, tags: string[] }`
 * @param {object} [options]   anything from PARAMETERS / FEATURE_FLAGS
 * @returns {object} a model with `suggest(tx)` and `explain(tx, label)`
 */
export function train(examples, options = {}) {
  const o = withDefaults(options)
  const rows = examples.map((e) => features(e.tx, o))

  const collect = (pick) => {
    const byLabel = new Map()
    examples.forEach((example, i) => {
      for (const label of pick(example)) {
        if (!label) continue
        if (!byLabel.has(label)) byLabel.set(label, new Set())
        byLabel.get(label).add(i)
      }
    })
    // A label seen once is a coincidence; training on it teaches the model one
    // description by heart and nothing else.
    return new Map([...byLabel].filter(([, seen]) => seen.size >= o.minExamples))
  }

  const categoryLabels = collect((e) => [e.category])
  const tagLabels = collect((e) => e.tags ?? [])

  let seed = 1
  const fit = (labels) =>
    new Map([...labels].map(([label, positives]) => [label, trainOne(rows, positives, o, seed++)]))

  const categories = fit(categoryLabels)
  const tags = fit(tagLabels)

  /**
   * Every word the ledger has ever shown the model.
   *
   * Without this a description made of words it has never seen still scores —
   * on the bias and the amount alone — and comes back as whatever you spend on
   * most. That is the base rate, not a statement about *this* row, and offering
   * it as a suggestion would be a confident-looking guess with nothing behind
   * it. A suggestion has to mean "I recognise this".
   */
  const vocabulary = new Set()
  for (const row of rows) {
    for (const name of row.keys()) if (/^[wbp]:/.test(name)) vocabulary.add(name)
  }
  const recognises = (x) => {
    for (const name of x.keys()) if (/^[wbp]:/.test(name) && vocabulary.has(name)) return true
    return false
  }

  const model = {
    options: o,
    trainedOn: examples.length,
    vocabulary: vocabulary.size,
    categories: [...categories.keys()],
    tags: [...tags.keys()],

    /**
     * What this transaction probably is.
     * @returns {{category: {name, confidence}|null, tags: {id, confidence}[]}}
     */
    suggest(tx) {
      const x = features(tx, o)
      if (!recognises(x)) return { category: null, tags: [] }
      // A transaction has one category, so the best one wins outright rather
      // than every category above the line being offered at once.
      let best = null
      for (const [name, weights] of categories) {
        const confidence = score(weights, x)
        if (!best || confidence > best.confidence) best = { name, confidence }
      }
      const picked = []
      for (const [id, weights] of tags) {
        const confidence = score(weights, x)
        if (confidence >= o.threshold) picked.push({ id, confidence })
      }
      picked.sort((a, b) => b.confidence - a.confidence)
      return {
        category: best && best.confidence >= o.threshold ? best : null,
        tags: picked.slice(0, o.maxTags),
      }
    },

    /** The words that decided, largest first — why, not just what. */
    explain(tx, label, kind = 'category') {
      const weights = (kind === 'tag' ? tags : categories).get(label)
      if (!weights) return []
      return [...features(tx, o)]
        .map(([name, value]) => ({ feature: name, weight: round2((weights.get(name) ?? 0) * value) }))
        .filter((f) => f.weight !== 0)
        .sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight))
    },
  }
  return model
}

/**
 * How well those settings actually do, measured on rows the model never saw.
 *
 * The split is by position rather than at random: a ledger is a timeline, and
 * testing on the most recent slice is the question actually being asked — will
 * it get *the next one* right.
 *
 * @returns {object} counts and the rates derived from them
 */
export function evaluate(examples, options = {}, holdout = 0.25) {
  const o = withDefaults(options)
  const cut = Math.floor(examples.length * (1 - holdout))
  const train_ = examples.slice(0, cut)
  const test = examples.slice(cut)
  if (train_.length < 5 || test.length < 1) {
    return { enough: false, tested: test.length, trained: train_.length }
  }

  const model = train(train_, o)
  let categoryAsked = 0
  let categoryRight = 0
  let categoryCould = 0
  let tagHit = 0
  let tagSuggested = 0
  let tagWanted = 0

  for (const example of test) {
    const guess = model.suggest(example.tx)
    if (example.category) categoryCould++
    if (guess.category) {
      categoryAsked++
      if (guess.category.name === example.category) categoryRight++
    }
    const wanted = new Set(example.tags ?? [])
    tagWanted += wanted.size
    tagSuggested += guess.tags.length
    for (const tag of guess.tags) if (wanted.has(tag.id)) tagHit++
  }

  const rate = (top, bottom) => (bottom > 0 ? round2((top / bottom) * 100) / 100 : null)
  const precision = rate(tagHit, tagSuggested)
  const recall = rate(tagHit, tagWanted)
  return {
    enough: true,
    trained: train_.length,
    tested: test.length,
    category: {
      // Of the ones it spoke up about, how often it was right.
      precision: rate(categoryRight, categoryAsked),
      // And how much of the work it was willing to do at all.
      coverage: rate(categoryAsked, categoryCould),
      asked: categoryAsked,
      right: categoryRight,
    },
    tags: {
      precision,
      recall,
      f1:
        precision && recall && precision + recall > 0
          ? round2(((2 * precision * recall) / (precision + recall)) * 100) / 100
          : null,
      suggested: tagSuggested,
      hit: tagHit,
      wanted: tagWanted,
    },
  }
}
